import React from 'react';
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { UserSegment } from '../../../app/network/api/dashboard';

interface UserSegmentChartProps {
    data: UserSegment[];
    loading?: boolean;
}

const COLORS = ['#ef5350', '#ffa726', '#66bb6a', '#42a5f5'];

const UserSegmentChart: React.FC<UserSegmentChartProps> = ({ data, loading }) => {
    const theme = useTheme();

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        사용자 활동 세그먼트
                    </Typography>
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="text.secondary">로딩 중...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    const chartData = data.map((item) => ({
        name: item.segment,
        value: item.userCount,
        percentage: item.percentage,
    }));

    const renderCustomLabel = ({
        cx,
        cy,
        midAngle,
        innerRadius,
        outerRadius,
        percent,
    }: any) => {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
        const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                style={{ fontSize: 14, fontWeight: 600 }}
            >
                {`${(percent * 100).toFixed(1)}%`}
            </text>
        );
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    사용자 활동 세그먼트
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    최근 7일 활동 기준
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={renderCustomLabel}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            contentStyle={{
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 4,
                            }}
                            formatter={(value: number, name: string, props: any) => [
                                `${value.toLocaleString()}명 (${props.payload.percentage.toFixed(1)}%)`,
                                name,
                            ]}
                        />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default UserSegmentChart;
